// Rolling-stock checklist aggregator. Loads materials + clears from the
// auth-only sidecar tables (mig 048) and folds them into a per-equipment
// "what's coming due in the next maintenance window" view.
//
// Window per Codex:
//   - hours-tracked equipment: current_reading + 100h
//   - km-tracked equipment   : current_reading + 5000km
//   - 'use'-interval materials: always present (until manually cleared)
//
// Clear bucket math (Codex amendment 4):
//   For hours/km intervals, the bucket = computeIntervalStatus's next_due
//   milestone for that interval. With NO completion history (e.g. fresh
//   piece at current=80 with a 50h interval), next_due=50 and the interval
//   is overdue — bucket="50". After a completion at 80 snaps to milestone
//   100, the next firing is bucket=150. A clear keyed to bucket=50 no
//   longer matches once the snap shifts the next_due forward — material
//   reappears in the new cycle.
//
//   For 'use' intervals, due_bucket_value=NULL, due_bucket_unit='use';
//   clears persist until admin manually un-clears via the editor.
//
// Identity per Codex amendment 3: (source_kind, interval_unit,
// interval_value, attachment_name) is the structural key. service_label is
// for display only — never join on it.

import {computeIntervalStatus, latestSaneReading} from './equipment.js';
// service_intervals rows are owned by src/lib/equipment.ts — it does the
// scheduling maths and this module feeds the column straight into it, so the
// shape is declared there once and imported rather than described twice.
import type {ServiceInterval} from './equipment.js';

export type {ServiceInterval};

// Row shapes below name ONLY the columns this aggregator reads. Numeric columns
// admit `string` as well as `number` because several arrive from jsonb blobs
// (service_intervals, attachment_checklists) rather than typed table columns,
// and every read already goes through Number(). Everything else stays `unknown`
// behind an index signature - there are no generated database types here.

// One entry of equipment.attachment_checklists (jsonb).
export type AttachmentChecklist = {
  name?: string | null;
  kind?: string | null;
  hours_or_km?: number | string | null;
  label?: string | null;
};

// One entry of equipment_fuelings.service_intervals_completed (jsonb), plus the
// reading_at_completion this module stamps onto it.
export type IntervalCompletion = {
  attachment_name?: string | null;
  reading_at_completion?: number | null;
  [field: string]: unknown;
};

// What computeIntervalStatus (src/lib/equipment.js) returns, as far as THIS
// module reads it: the interval's identity plus its due state.
export type IntervalStatus = {
  kind?: string | null;
  hours_or_km?: number | string | null;
  next_due?: number | null;
  until_due?: number | null;
  overdue?: boolean;
  [field: string]: unknown;
};

export type EquipmentRow = {
  id: string;
  name?: string | null;
  status?: string | null;
  tracking_unit?: string | null;
  service_intervals?: unknown;
  attachment_checklists?: unknown;
  [column: string]: unknown;
};

export type FuelingRow = {
  date?: string | null;
  hours_reading?: number | string | null;
  km_reading?: number | string | null;
  service_intervals_completed?: unknown;
  [column: string]: unknown;
};

// equipment_service_materials (mig 048).
export type MaterialRow = {
  id: string;
  equipment_id?: string | null;
  active?: boolean | null;
  source_kind?: string | null;
  interval_unit?: string | null;
  interval_value?: number | string | null;
  attachment_name?: string | null;
  service_label?: string | null;
  material_name?: string | null;
  sort_order?: number | null;
  [column: string]: unknown;
};

// equipment_material_clears (mig 048).
export type MaterialClearRow = {
  material_id?: string | null;
  due_bucket_value?: number | string | null;
  due_bucket_unit?: string | null;
  [column: string]: unknown;
};

// One service group in the rendered checklist.
export type MaterialGroup = {
  groupKey: string;
  source_kind?: string | null;
  interval_unit?: string | null;
  interval_value?: number | string | null;
  attachment_name?: string | null;
  service_label?: string | null;
  status: IntervalStatus | null;
  due_bucket_value: number | null;
  due_bucket_unit: string | null | undefined;
  materials: MaterialRow[];
};

export type EquipmentChecklist = {
  equipment: EquipmentRow;
  current_reading: number;
  groups: MaterialGroup[];
};

// Number.isFinite is declared `(value: unknown) => boolean` and therefore does
// NOT narrow. This predicate is exactly equivalent at runtime - Number.isFinite
// is true only for a finite `number` - and carries the narrowing the arithmetic
// below needs without a cast.
const isFiniteNumber = (value: unknown): value is number => Number.isFinite(value);

export const HOURS_WINDOW = 100;
export const KM_WINDOW = 5000;

const INTERVAL_UNIT_ORDER: Record<string, number | undefined> = {hours: 0, km: 1, use: 2};

function numOrMax(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

function textCompare(a: unknown, b: unknown): number {
  return String(a || '').localeCompare(String(b || ''));
}

function compareMaterialGroups(a: MaterialGroup, b: MaterialGroup): number {
  const ao = a.status?.overdue ? 0 : 1;
  const bo = b.status?.overdue ? 0 : 1;
  if (ao !== bo) return ao - bo;
  const ad = a.status?.next_due ?? a.status?.until_due ?? 1e9;
  const bd = b.status?.next_due ?? b.status?.until_due ?? 1e9;
  if (ad !== bd) return ad - bd;
  const ak = a.source_kind === 'service_interval' ? 0 : 1;
  const bk = b.source_kind === 'service_interval' ? 0 : 1;
  if (ak !== bk) return ak - bk;
  // String() reproduces exactly what a JavaScript property access already did
  // with a null/undefined key ('null' / 'undefined' -> undefined -> 9).
  const au = INTERVAL_UNIT_ORDER[String(a.interval_unit)] ?? 9;
  const bu = INTERVAL_UNIT_ORDER[String(b.interval_unit)] ?? 9;
  if (au !== bu) return au - bu;
  const av = numOrMax(a.interval_value);
  const bv = numOrMax(b.interval_value);
  if (av !== bv) return av - bv;
  const at = textCompare(a.attachment_name, b.attachment_name);
  if (at !== 0) return at;
  const al = textCompare(a.service_label, b.service_label);
  if (al !== 0) return al;
  return textCompare(a.groupKey, b.groupKey);
}

// Build completions list shaped for computeIntervalStatus from the raw
// equipment_fuelings rows. Mirrors the inline build in
// EquipmentFuelingWebform.jsx (the historical convention) — each completion
// gets a reading_at_completion stamped from the parent fueling's hours/km.
//
// Returns the FULL combined completion list (main + attachment-tagged); the
// caller filters by `attachment_name` per-source so that a 50h AERO-Vator
// completion does NOT advance the main 50h service bucket and vice-versa.
function buildCompletions(
  eq: EquipmentRow | null | undefined,
  fuelings: FuelingRow[] | null | undefined,
): IntervalCompletion[] {
  const unit = eq?.tracking_unit === 'km' ? 'km' : 'hours';
  const out: IntervalCompletion[] = [];
  for (const f of fuelings || []) {
    const completed: IntervalCompletion[] = Array.isArray(f?.service_intervals_completed)
      ? f.service_intervals_completed
      : [];
    if (completed.length === 0) continue;
    const reading =
      unit === 'km'
        ? f.km_reading != null
          ? Number(f.km_reading)
          : null
        : f.hours_reading != null
          ? Number(f.hours_reading)
          : null;
    for (const c of completed) {
      out.push({...c, reading_at_completion: isFiniteNumber(reading) ? reading : null});
    }
  }
  return out;
}

// Build a map keyed by structural identity that the materials aggregator
// uses to look up the right interval status row for each material.
//
//   key = `${source_kind}|${interval_unit}|${interval_value ?? ''}|${attachment_name ?? ''}`
//
// Per-source completion split (Codex review): EquipmentFuelingWebform writes
// service_intervals_completed entries with `attachment_name: <name>` for
// attachment ticks and without that key for main-service ticks. The math
// must respect that split:
//   - Service-interval status uses ONLY completions with no attachment_name.
//     Otherwise an AERO-Vator 50h completion would also satisfy the main
//     50h bucket.
//   - Each attachment runs its own computeIntervalStatus pass over that
//     attachment's intervals + that attachment's completions only. Without
//     this, attachment materials would be hardcoded to never-completed
//     and stay forever in bucket=interval_value once current crossed it —
//     a stale clear in that bucket would suppress the material indefinitely
//     across rolling cycles.
//
// Materials with interval_unit='use' don't have a status row — they're
// always-due unless cleared (handled in inWindow()).
function buildIntervalStatusByKey(
  eq: EquipmentRow | null | undefined,
  fuelings: FuelingRow[] | null | undefined,
  currentReading: number,
): Map<string, IntervalStatus> {
  const intervals: ServiceInterval[] = Array.isArray(eq?.service_intervals) ? eq.service_intervals : [];
  const allCompletions = buildCompletions(eq, fuelings);
  const mainCompletions = allCompletions.filter((c) => !c.attachment_name);
  const byKey = new Map<string, IntervalStatus>();

  const mainStatuses = computeIntervalStatus(intervals, mainCompletions, currentReading);
  for (const s of mainStatuses) {
    const key = `service_interval|${s.kind}|${s.hours_or_km}|`;
    byKey.set(key, s);
  }

  // Group attachments by name so each name-scoped intervals list runs its
  // own computeIntervalStatus pass with its own completion subset.
  const attachments: AttachmentChecklist[] = Array.isArray(eq?.attachment_checklists) ? eq.attachment_checklists : [];
  const intervalsByAttachment = new Map<string, ServiceInterval[]>();
  for (const a of attachments) {
    const value = Number(a.hours_or_km);
    if (!Number.isFinite(value) || value <= 0) continue; // Every-Use sentinel handled separately in inWindow()
    const name = a.name || '';
    const kind = a.kind === 'km' ? 'km' : 'hours';
    const arr = intervalsByAttachment.get(name) || [];
    arr.push({kind, hours_or_km: value, label: a.label || ''});
    intervalsByAttachment.set(name, arr);
  }
  for (const [name, attachmentIntervals] of intervalsByAttachment) {
    const attachmentCompletions = allCompletions.filter((c) => c.attachment_name === name);
    const statuses = computeIntervalStatus(attachmentIntervals, attachmentCompletions, currentReading);
    for (const s of statuses) {
      const key = `attachment_checklist|${s.kind}|${s.hours_or_km}|${name}`;
      byKey.set(key, s);
    }
  }
  return byKey;
}

// Decide whether a material is in the rolling window for the given interval
// status. 'use' materials are always in-window. Returns the due_bucket
// {value, unit} for clear-key lookup.
function inWindow(
  material: MaterialRow,
  status: IntervalStatus | null,
): {inWindow: boolean; dueBucketValue: number | null; dueBucketUnit: string | null | undefined} {
  if (material.interval_unit === 'use') {
    return {inWindow: true, dueBucketValue: null, dueBucketUnit: 'use'};
  }
  if (!status) {
    return {inWindow: false, dueBucketValue: null, dueBucketUnit: material.interval_unit};
  }
  const window = material.interval_unit === 'km' ? KM_WINDOW : HOURS_WINDOW;
  const within = status.overdue || (status.until_due != null && status.until_due <= window);
  return {
    inWindow: within,
    dueBucketValue: status.next_due ?? null,
    dueBucketUnit: material.interval_unit,
  };
}

// Bucket equality with NULL handling (NULLS NOT DISTINCT semantics).
function bucketEquals(clear: MaterialClearRow, value: number | null, unit: string | null | undefined): boolean {
  if (clear.due_bucket_unit !== unit) return false;
  if (value == null && clear.due_bucket_value == null) return true;
  if (value == null || clear.due_bucket_value == null) return false;
  return Number(clear.due_bucket_value) === Number(value);
}

// Aggregate materials by equipment → service group → rows. Cleared rows are
// omitted entirely (Codex amendment 2 — "vanish from the active list").
//
// Inputs:
//   equipment    Array<equipment row>            — active only; fed in by caller.
//   fuelingsBy   Map<equipment_id, Array>        — per-piece equipment_fuelings.
//   materials    Array<material row>             — equipment_service_materials.
//   clears       Array<clear row>                — equipment_material_clears.
//
// Output: array of { equipment, groups: [ { groupKey, label, status, materials:[...] } ] }.
// Empty equipment (no in-window non-cleared materials) is omitted.
export function buildMaterialChecklist({
  equipment,
  fuelingsBy,
  materials,
  clears,
}: {
  equipment?: EquipmentRow[] | null;
  fuelingsBy?: Map<string, FuelingRow[]> | Record<string, FuelingRow[]> | null;
  materials?: MaterialRow[] | null;
  clears?: MaterialClearRow[] | null;
}): EquipmentChecklist[] {
  const fuelingsMap = fuelingsBy instanceof Map ? fuelingsBy : new Map(Object.entries(fuelingsBy || {}));
  const matsByEq = new Map<string, MaterialRow[]>();
  for (const m of materials || []) {
    if (!m.active) continue;
    // String() on the map key reproduces what a JavaScript Map keyed by these
    // ids already did for a null/undefined equipment_id: one shared bucket. No
    // lookup below can reach it, because every equipment row has an id.
    const arr = matsByEq.get(String(m.equipment_id)) || [];
    arr.push(m);
    matsByEq.set(String(m.equipment_id), arr);
  }
  const clearsByMat = new Map<string, MaterialClearRow[]>();
  for (const c of clears || []) {
    const arr = clearsByMat.get(String(c.material_id)) || [];
    arr.push(c);
    clearsByMat.set(String(c.material_id), arr);
  }

  const out: EquipmentChecklist[] = [];
  for (const eq of equipment || []) {
    if (eq.status !== 'active') continue;
    const eqMaterials = matsByEq.get(eq.id) || [];
    if (eqMaterials.length === 0) continue;
    const fuelings = fuelingsMap.get(eq.id) || [];
    const currentReading = latestSaneReading(eq, fuelings);
    const byKey = buildIntervalStatusByKey(eq, fuelings, currentReading);

    // Group by structural service identity. Same identity used by the
    // materials table's UNIQUE INDEX (Codex amendment 3) so the grouping
    // stays stable across label edits.
    const groups = new Map<string, MaterialGroup>();
    for (const m of eqMaterials) {
      const groupKey = `${m.source_kind}|${m.interval_unit}|${m.interval_value ?? ''}|${m.attachment_name ?? ''}`;
      const status = byKey.get(groupKey) || null;
      const {inWindow: ok, dueBucketValue, dueBucketUnit} = inWindow(m, status);
      if (!ok) continue;
      // Skip if cleared in this bucket.
      const matClears = clearsByMat.get(m.id) || [];
      const cleared = matClears.some((c) => bucketEquals(c, dueBucketValue, dueBucketUnit));
      if (cleared) continue;
      const g: MaterialGroup = groups.get(groupKey) || {
        groupKey,
        source_kind: m.source_kind,
        interval_unit: m.interval_unit,
        interval_value: m.interval_value,
        attachment_name: m.attachment_name,
        service_label: m.service_label,
        status,
        due_bucket_value: dueBucketValue,
        due_bucket_unit: dueBucketUnit,
        materials: [],
      };
      g.materials.push(m);
      groups.set(groupKey, g);
    }
    if (groups.size === 0) continue;
    // Sort materials within each group by sort_order then name.
    for (const g of groups.values()) {
      g.materials.sort((a, b) => {
        const oa = isFiniteNumber(a.sort_order) ? a.sort_order : 0;
        const ob = isFiniteNumber(b.sort_order) ? b.sort_order : 0;
        if (oa !== ob) return oa - ob;
        return (a.material_name || '').localeCompare(b.material_name || '');
      });
    }
    // Sort groups by due priority, then structural identity so a Clear/refetch
    // cannot shuffle equal-due checklist groups.
    const sortedGroups = Array.from(groups.values()).sort(compareMaterialGroups);
    out.push({equipment: eq, current_reading: currentReading, groups: sortedGroups});
  }
  // Sort equipment alphabetically by name for stable display.
  out.sort((a, b) => (a.equipment.name || '').localeCompare(b.equipment.name || ''));
  return out;
}
